import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
// oxlint-disable-next-line no-restricted-imports
import { useMatch, useParams } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { userId } from '@gonfalon/constants/constants';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { getDogfoodClient } from '@gonfalon/dogfood-client/getDogfoodClient';
import { enableRatioMetrics, metricWindowingFrontend } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { DataSourceType } from '@gonfalon/metrics/constants';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { HealthIndicator } from '@gonfalon/metrics/MetricHealthCheck';
import { MetricKind } from '@gonfalon/metrics/MetricKind';
import { toMetricEventUrl } from '@gonfalon/metrics/toMetricEventUrl';
import type { Metric, MetricEventUrl } from '@gonfalon/metrics/types';
import { NavigationPromptModal } from '@gonfalon/navigation-prompt/modal';
import { genericCheckAccess } from '@gonfalon/permissions/access';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { getMetricEventsQuery } from '@gonfalon/rest-api/queries/metricEvents';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Alert,
  Button,
  Dialog,
  Form,
  Heading,
  Label,
  Link,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Popover,
  ProgressBar,
  Select,
  SelectValue,
  ToggleButton,
  ToggleButtonGroup,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { merge } from 'lodash';

import { RequiredLabel } from './components/RequiredLabel';
import { SEARCH_RESULT_DISPLAY_NUMBER } from './EventActivitySearchResults/EventActivitySearchResults';
import { EventSourceField } from './EventSourceField';
import type { DenominatorInput, MetricForm } from './formUtils';
import { MetricDataSourceFields } from './MetricDataSourceFields';
import { MetricFooterFields } from './MetricFooterFields';
import { MetricKindDetails } from './MetricKindDetails';
import { MetricMetadata } from './MetricMetadata';
import { MetricWindowingParams, toWindowOffsets } from './MetricWindowingParams/MetricWindowingParams';
import { RatioMetricSection } from './RatioMetricSection';
import { toRatioWindowOffsets } from './RatioTermSection';
import { useCreateMetric } from './useCreateMetric';
import { WinsorizationFields } from './WinsorizationFields/WinsorizationFields';

import styles from './CreateMetricModal.module.css';

// need to include every field or `isDirty` may be a false positive
const staticDefaultFormValues: MetricForm = {
  analysisType: 'mean',
  kind: MetricKind.CUSTOM,
  isNumeric: false,
  urls: [{ kind: 'canonical', value: '' }],
  randomizationUnits: [],
  maintainerId: '',
  name: '',
  key: '',
  description: '',
  eventKey: '',
  tags: undefined,
  successCriteria: 'HigherThanBaseline',
  unitAggregationType: undefined,
  percentileValue: undefined,
  eventDefault: { disabled: false, value: 0 },
  dataSourceKey: DataSourceType.LAUNCHDARKLY_HOSTED,
  windowMode: 'none',
  windowUnit: 'days',
  windowStart: undefined,
  windowEnd: undefined,
  ratioOrSimple: 'simple',
  unitAggregationField: undefined,
  denominator: undefined,
  winsorization: undefined,
};

export function CreateMetricModal({
  onCancel,
  onSuccess,
  defaultValues,
}: {
  onCancel: () => void;
  onSuccess?: (metric: Metric) => void;
  defaultValues?: Partial<MetricForm>;
}) {
  const projectKey = useProjectKey();
  const client = useQueryClient();
  const openAtStamp = useRef(Date.now());
  const { profile } = useAccountContext();
  const hasAccess = genericCheckAccess('metric', profile, 'createMetric', profile._access).isAllowed;

  const [showPrompt, setShowPrompt] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [closeMethod, setCloseMethod] = useState<'cancel button' | undefined>(undefined);
  const { mutate: createMetric, isPending: isSaving } = useCreateMetric();
  const form = useForm<MetricForm>({
    defaultValues: merge({}, staticDefaultFormValues, {
      maintainerId: userId(),
      ...defaultValues,
    }),
    mode: 'all',
  });

  const { isDirty, isValid, dirtyFields } = form.formState;
  const hasErrors = Boolean(Object.keys(form.formState.errors).length);
  const params = useParams();
  const matchBuilder = useMatch({ path: '/projects/:projKey/experiments/create' });
  const matchMetrics = useMatch({ path: '/projects/:projKey/metrics/new' });
  const matchObserve = useMatch({ path: '/projects/:projKey/observe/*' });
  const matchHoldouts = useMatch({ path: '/projects/:projKey/holdouts/*' });

  const getLocation = () => {
    if (params.experimentKey) {
      return 'design';
    }
    if (matchBuilder) {
      return 'builder';
    }
    if (matchObserve) {
      return 'eventExplorer';
    }
    if (matchMetrics) {
      return 'metricsList';
    }
    if (matchHoldouts) {
      return 'holdouts';
    }
    return 'unknown';
  };

  const control = form.control;
  useEffect(() => {
    if (hasAccess) {
      trackMetricsEvent('Create Metric Modal Opened', {
        location: getLocation(),
      });
    }
  }, []);

  // Allow the parent component to pass in defaultValues on a subsequent render.
  // This handles the case where defaultValues are sourced from a network request,
  // meaning the defaultValue prop is undefined on the first render.
  const prevDefaultValues = useRef(defaultValues);
  useEffect(() => {
    // Only merge in deferred default values once,
    if (prevDefaultValues.current === undefined && typeof defaultValues === 'object') {
      for (const [key, defaultValue] of Object.entries(defaultValues)) {
        const fieldKey = key as keyof MetricForm;

        // Taking care not to overwrite any fields the user has already modified
        if (!dirtyFields[fieldKey]) {
          form.setValue(fieldKey, defaultValue);
        }
      }
    }

    prevDefaultValues.current = defaultValues;
  }, [form, dirtyFields, defaultValues]);

  const isMetricWindowingEnabled = metricWindowingFrontend();
  const metricKind = useWatch({ control, name: 'kind', exact: true });
  const ratioOrSimple = useWatch({ control, name: 'ratioOrSimple', exact: true }) ?? 'simple';
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));

  const isRatioMetricsEnabled = useMemo(
    () => enableRatioMetrics() && (dataSourcesData?.items?.length ?? 0) > 0,
    [dataSourcesData],
  );

  const isRatioMode = isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && ratioOrSimple === 'ratio';

  // When entering ratio mode, auto-switch away from LD-hosted to the first available warehouse source
  useEffect(() => {
    if (isRatioMode && isLDHosted(dataSourceKeyFormValue)) {
      const firstWarehouse = dataSourcesData?.items?.find((ds) => Boolean(ds.environmentKey));
      if (firstWarehouse) {
        handleEventSourceChange({ dataSourceKey: firstWarehouse.key, kind: MetricKind.CUSTOM });
        // Sync the denominator to the same warehouse source so both selectors start in sync
        const denom = form.getValues('denominator');
        if (denom) {
          form.setValue('denominator.dataSourceKey', firstWarehouse.key, { shouldDirty: false });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatioMode, dataSourcesData]);

  // When entering ratio mode the simple-form fields (measureType, unitAggregationType) unmount
  // but React Hook Form keeps their `required` rules registered by default, leaving `isValid`
  // false with no visible field to fix. Explicitly unregister them so the form can be submitted.
  useEffect(() => {
    if (isRatioMode) {
      form.unregister(['measureType', 'unitAggregationType']);
    }
  }, [isRatioMode, form]);
  const isSnowflakeExperimentationSource = useMemo(
    () =>
      dataSourcesData?.items?.find((ds) => ds.key === dataSourceKeyFormValue)?.integrationKey ===
      'snowflake-experimentation',
    [dataSourcesData, dataSourceKeyFormValue],
  );

  const eventKeyFormValue = useWatch({ control, name: 'eventKey' });
  const isNumericFormValue = useWatch({ control, name: 'isNumeric' });
  const measureTypeFormValue = useWatch({ control, name: 'measureType' });
  const unitAggregationTypeFormValue = useWatch({ control, name: 'unitAggregationType' });
  const successCriteriaFormValue = useWatch({
    control,
    name: 'successCriteria',
    exact: true,
  });

  const formRandomizationUnits = useWatch({
    control,
    name: 'randomizationUnits',
    exact: true,
  }) as Array<{ value: string }>;

  const randomizationUnits = formRandomizationUnits?.map((u) => u.value) || [];

  const handleEventSourceChange = useCallback(
    ({
      dataSourceKey,
      kind,
    }: {
      dataSourceKey: MetricForm['dataSourceKey'];
      kind: Extract<MetricKind, MetricKind.TRACE | MetricKind.CUSTOM>;
    }) => {
      if (kind !== MetricKind.TRACE) {
        form.clearErrors('traceQuery');
        form.setValue('traceQuery', undefined);
        form.setValue('traceValueLocation', undefined);
      }

      if (isLDHosted(dataSourceKey)) {
        form.clearErrors('dataSourceKey');
        form.setValue('windowMode', 'none');
        form.setValue('windowStart', undefined);
        form.setValue('windowEnd', undefined);
      } else {
        // custom is the only kind supported for non-LD data sources
        // percentile metrics are yet to be implemented for non-LD data sources
        form.setValue('analysisType', 'mean');
      }
      // reset the eventKey, as its unlikely there will be a matching key in the
      // alternative data source
      form.setValue('eventKey', '');
      form.setValue('kind', kind);
      form.setValue('dataSourceKey', dataSourceKey);
      form.reset(form.getValues(), { keepValues: true, keepDirty: true, keepTouched: true });
    },
    [form],
  );

  const onValid = (metric: MetricForm) => {
    createMetric(toMetricPost(metric), {
      onSuccess: (m) => {
        const successAtStamp = Date.now();
        const modalUseDuration = (successAtStamp - openAtStamp.current) / 1000;
        getDogfoodClient().track('Metric Creation Modal Duration', undefined, modalUseDuration);
        if (onSuccess) {
          onSuccess(m);
          trackLastSeenEventCount();
          trackMetricsEvent('Metric Creation Success', {
            metricKey: m.key,
            location: getLocation(),
          });
          if (m.filters) {
            trackMetricsEvent('Metric Created With Filters', {
              metricKey: m.key,
              filters: m.filters,
              eventKey: m.eventKey,
            });
          }
          setIsModalOpen(false);
          return;
        }
        onCancel();
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        trackMetricsEvent('Metric Creation Error', {
          location: getLocation(),
          message: errorMessage,
          metric,
        });
      },
    });
    trackMetricsEvent('Create Metric Modal Submitted', {
      metricKey: metric.key,
      location: getLocation(),
    });
  };

  const trackLastSeenEventCount = () => {
    const data = client.getQueryData(
      getMetricEventsQuery({
        projectKey,
        query: {
          q: eventKeyFormValue,
          limit: SEARCH_RESULT_DISPLAY_NUMBER,
        },
      }).queryKey,
    );

    if (data) {
      trackMetricsEvent('Events Last Fetched Count', {
        metricValue: data.items.length,
        value: data.items.length,
      });
    }
  };
  const onCancelWithPrompt = (closeLocation?: 'cancel button') => {
    if (isDirty) {
      setShowPrompt(true);
      setCloseMethod(closeLocation);
    } else {
      const closeLoc = closeLocation || closeMethod;
      handleModalExit(closeLoc);
      setCloseMethod(undefined);
    }
  };

  const handlePromptCancel = () => {
    setShowPrompt(false);
    setCloseMethod(undefined);
  };

  const handlePromptComfirm = () => {
    handleModalExit();
  };

  const handleModalExit = (closeLocation?: 'cancel button') => {
    const closeLoc = closeLocation || closeMethod;
    trackMetricsEvent('Create Metric Modal Closed', {
      location: getLocation(),
      closeMethod: closeLoc || 'background click',
    });
    setIsModalOpen(false);
    onCancel();
  };
  const formId = useId();

  return (
    <FormProvider {...form}>
      <ModalOverlay isOpen={isModalOpen} onOpenChange={(open: boolean) => !open && !showPrompt && onCancelWithPrompt()}>
        <Modal isOpen={isModalOpen} className={isRatioMode ? styles.noPaddingRatio : styles.noPadding}>
          <Dialog className={isRatioMode ? styles.noPaddingRatio : styles.noPadding} data-test-id="create-metric-modal">
            {showPrompt ? (
              <NavigationPromptModal
                onCancelNavigation={handlePromptCancel}
                onConfirmNavigation={handlePromptComfirm}
                body="If you close this form, you will lose any unsaved changes."
                confirmText="Discard changes"
              />
            ) : null}
            <div slot="header" className={styles.header}>
              <Heading slot="title" className={styles.formTitle}>
                Create metric
              </Heading>
              <div className={styles.learnMoreLink}>
                Metrics measure audience behaviors and performance.{' '}
                <Link
                  href={getDocumentationUrl('home/observability/metrics')}
                  target="_blank"
                  className={styles.learnMoreLink}
                >
                  Read about metrics
                </Link>
                .
              </div>
              {!hasAccess && (
                <Alert isOpen status="error" className={styles.permissionAlert}>
                  <h3>Metric creation is disabled</h3>
                  <span>
                    Your user account does not have permission to create metrics. Please request the necessary
                    permissions from your administrator.
                  </span>
                </Alert>
              )}
            </div>
            <div slot="body" className={styles.formBody}>
              <ErrorBoundary
                severity="high"
                owner="team-metrics"
                fallback={
                  <Alert isOpen status="error">
                    <h3>Something went wrong</h3>
                    <span>An unexpected error occurred while loading the metric form. Please try again.</span>
                  </Alert>
                }
              >
                <Form
                  id={formId}
                  className={styles.createMetricForm}
                  data-test-id="metric-form"
                  onSubmit={form.handleSubmit(onValid)}
                >
                  {isRatioMode && (
                    <div className={styles.ratioModeForm}>
                      {isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && (
                        <RatioOrSimpleMetricToggle control={control} />
                      )}
                      <EventSourceField control={control} onChange={handleEventSourceChange} />
                      <RatioMetricSection context={form} />
                      <MetricMetadata context={form} fullWidth />
                    </div>
                  )}
                  {!isRatioMode && (
                    <div className={styles.simpleModeForm}>
                      {isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && (
                        <RatioOrSimpleMetricToggle control={control} />
                      )}
                      <EventSourceField control={control} onChange={handleEventSourceChange} isDisabled={false} />
                      <MetricDataSourceFields control={control} showCreateCTA />
                      {isLDHosted(dataSourceKeyFormValue) && metricKind !== 'trace' && (
                        <MetricKindPicker control={control} />
                      )}
                      <MetricKindDetails context={form} metricKind={metricKind} />
                      {isMetricWindowingEnabled && isSnowflakeExperimentationSource && (
                        <MetricWindowingParams context={form} />
                      )}
                      <WinsorizationFields context={form} />
                      <MetricMetadata context={form} />
                    </div>
                  )}
                </Form>
              </ErrorBoundary>
            </div>
            <div slot="footer" className={styles.footer}>
              <MetricFooterFields context={form} />
              <div className={styles.footerActions}>
                {metricKind === MetricKind.CUSTOM && !isRatioMode && (
                  <HealthIndicator
                    metric={{
                      kind: metricKind,
                      eventKey: eventKeyFormValue || '',
                      dataSource: dataSourceKeyFormValue ? { key: dataSourceKeyFormValue } : undefined,
                      randomizationUnits:
                        ((measureTypeFormValue && measureTypeFormValue !== 'value') ||
                          !!unitAggregationTypeFormValue) &&
                        randomizationUnits.length > 0
                          ? randomizationUnits
                          : [],
                      isNumeric: !!isNumericFormValue || false,
                      _versionId: '',
                      successCriteria: successCriteriaFormValue,
                      unitAggregationType: unitAggregationTypeFormValue,
                    }}
                  />
                )}
                <Button onPress={() => onCancelWithPrompt('cancel button')}>Cancel</Button>
                <Button
                  form={formId}
                  variant="primary"
                  type="submit"
                  isDisabled={hasErrors || !isValid || isSaving || !hasAccess}
                  data-test-id="metric-create-button"
                >
                  Create
                  {isSaving && <ProgressBar aria-label="Loading…" isIndeterminate size="small" />}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </FormProvider>
  );
}

interface RatioOrSimpleMetricToggleProps {
  control: Control<MetricForm>;
}

export const RatioOrSimpleMetricToggle: React.FC<RatioOrSimpleMetricToggleProps> = ({ control }) => {
  const ratioOrSimple = useWatch({ control, name: 'ratioOrSimple', exact: true }) ?? 'simple';
  const { getValues, setValue } = useFormContext<MetricForm>();

  return (
    <Controller
      control={control}
      name="ratioOrSimple"
      render={({ field }) => (
        <div className={styles.ratioOrSimpleTogglePicker}>
          <Label>Select metric structure</Label>
          <ToggleButtonGroup
            selectedKeys={[ratioOrSimple]}
            onSelectionChange={(keys) => {
              const newType = [...keys][0];
              if (newType === 'simple' || newType === 'ratio') {
                field.onChange(newType);
                if (getValues('dataSourceKey') === undefined) {
                  setValue('dataSourceKey', DataSourceType.LAUNCHDARKLY_HOSTED);
                }
              }
            }}
            disallowEmptySelection
            className={styles.ratioOrSimpleToggle}
          >
            <ToggleButton id="simple">Simple</ToggleButton>
            <ToggleButton id="ratio">Ratio</ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
    />
  );
};

interface MetricKindPickerProps {
  control: Control<MetricForm>;
}

const MetricKindPicker: React.FC<MetricKindPickerProps> = ({ control }) => {
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });

  return (
    <Controller
      control={control}
      name="kind"
      render={({ field }) => (
        <Select
          onChange={field.onChange}
          value={field.value}
          className={styles.shortInput}
          isDisabled={!isLDHosted(dataSourceKeyFormValue)}
        >
          <RequiredLabel label="Event kind" htmlFor="kind" />
          <Button>
            <SelectValue />
            <Icon name="chevron-down" size="small" />
          </Button>
          <Popover>
            <ListBox>
              <ListBoxItem id={MetricKind.CUSTOM}>Custom</ListBoxItem>
              <ListBoxItem id={MetricKind.PAGEVIEW}>Page viewed</ListBoxItem>
              <ListBoxItem id={MetricKind.CLICK}>Clicked or tapped</ListBoxItem>
            </ListBox>
          </Popover>
        </Select>
      )}
    />
  );
};

export type MetricPost = Omit<
  MetricForm,
  | 'tags'
  | 'randomizationUnits'
  | 'urls'
  | 'kind'
  | 'windowMode'
  | 'windowUnit'
  | 'windowStart'
  | 'windowEnd'
  | 'ratioOrSimple'
  | 'denominator'
  | 'dataSourceKey'
  | 'winsorization'
> & {
  kind: 'click' | 'pageview' | 'custom' | 'trace';
  tags?: string[];
  randomizationUnits?: string[];
  urls?: MetricEventUrl[];
  windowStartOffset?: number;
  windowEndOffset?: number;
  denominator?: DenominatorInput;
  dataSource?: { key: string };
  isActive: boolean;
  winsorLowerPercentile?: number;
  winsorUpperPercentile?: number;
};

export function toMetricPost(form: MetricForm): MetricPost {
  const {
    kind,
    urls,
    randomizationUnits,
    tags,
    dataSourceKey,
    measureType: _measureType,
    windowMode,
    windowUnit,
    windowStart,
    windowEnd,
    ratioOrSimple: _ratioOrSimple,
    denominator: denominatorForm,
    winsorization,
    ...rest
  } = form;

  const isRatio = form.ratioOrSimple === 'ratio';

  const metric = {
    ...rest,
    isActive: true,
    kind,
    randomizationUnits: randomizationUnits?.map(({ value }) => value),
    tags: tags?.map(({ value }) => value),
    urls: kind === MetricKind.CLICK || kind === MetricKind.PAGEVIEW ? urls?.map(toMetricEventUrl) : undefined,
    filters: isLDHosted(form.dataSourceKey) ? form.filters : undefined,
    dataSource: dataSourceKey ? { key: dataSourceKey } : undefined,
    traceValueLocation: kind === 'trace' && form.isNumeric ? form.traceValueLocation : undefined,
    ...(windowMode === 'none'
      ? { windowStartOffset: undefined, windowEndOffset: undefined }
      : toWindowOffsets(windowStart, windowEnd, windowUnit)),
    unitAggregationField: `${rest.unitAggregationType}` === 'count_distinct' ? rest.unitAggregationField : undefined,
    denominator:
      isRatio && kind === MetricKind.CUSTOM && denominatorForm ? buildDenominatorPost(denominatorForm) : undefined,
    // Map UI winsorization fields to API format
    // Drop 0 and 100 values (they mean "no winsorization").
    // count_distinct is incompatible with winsorization — guard at serialization as a safety net.
    winsorLowerPercentile:
      `${rest.unitAggregationType}` !== 'count_distinct' &&
      winsorization?.lowerPercentile !== undefined &&
      winsorization.lowerPercentile !== 0
        ? winsorization.lowerPercentile
        : undefined,
    winsorUpperPercentile:
      `${rest.unitAggregationType}` !== 'count_distinct' &&
      winsorization?.upperPercentile !== undefined &&
      winsorization.upperPercentile !== 100
        ? winsorization.upperPercentile
        : undefined,
  };

  if (kind === MetricKind.CUSTOM) {
    metric.isNumeric = !!metric.isNumeric;
  }

  if (isRatio) {
    metric.unit = undefined;
    metric.eventDefault = { disabled: false, value: 0 };
  }

  return metric;
}

export function buildDenominatorPost(d: NonNullable<MetricForm['denominator']>): DenominatorInput {
  const windowOffsets = toRatioWindowOffsets(d.windowMode, d.windowStart, d.windowEnd, d.windowUnit);
  return {
    eventName: d.eventKey,
    isNumeric: d.aggregationType === 'sum' || d.aggregationType === 'average',
    unitAggregationType: d.aggregationType === 'count' ? 'sum' : d.aggregationType,
    unitAggregationField: d.aggregationType === 'count_distinct' ? d.unitAggregationField : undefined,
    dataSource: d.dataSourceKey ? { key: d.dataSourceKey } : undefined,
    ...windowOffsets,
    // count_distinct is incompatible with winsorization — guard at serialization as a safety net
    // in case form state was not cleared before submission.
    winsorLowerPercentile:
      d.aggregationType !== 'count_distinct' && d.winsorLowerEnabled && d.winsorLowerPercentile !== 0
        ? d.winsorLowerPercentile
        : undefined,
    winsorUpperPercentile:
      d.aggregationType !== 'count_distinct' && d.winsorUpperEnabled && d.winsorUpperPercentile !== 100
        ? d.winsorUpperPercentile
        : undefined,
  };
}
